/**
 * Etiketleri ham yazıcı komutu olarak basar (sessiz yazdırma).
 *
 * Tarayıcı yazdırma diyaloğu açılmaz; etiket canvas'a çizilip yazıcının kendi
 * diline çevrilir ve local-service üzerinden gönderilir.
 *
 * Akış: labelCanvasRenderer → mürekkep matrisi → yazıcı dili → USB/TCP/spooler
 *
 * Dil başına son adım farklı: PPLA görüntüyü 1-bit BMP olarak yazıcı belleğine
 * indirip yerleştiriyor, PPLB ham rasteri tek komutla görüntü tamponuna
 * yazıyor. Raster üretimi ikisinde de aynı.
 */

import { LabelTemplate, PlaceholderData, PrinterLanguage, mmToDots } from '../../../types/labelTemplate.types';
import { encodeBmp1Bit } from '../encoders/bmp1bit';
import { buildEplBitmapJob } from '../encoders/eplEncoder';
import { rasterizeLabels } from '../encoders/labelRaster';
import { buildPplaBitmapJob } from '../encoders/pplaEncoder';
import { buildZplBitmapJob } from '../encoders/zplEncoder';
import {
  LabelPrinterSelection,
  RAW_SUPPORTED_LANGUAGES,
  sendRawToPrinter,
} from '../../../services/labelPrintService';
import { LocalPrintError } from '../../../services/localPrintService';
import { createPerfAccumulator, perfLog } from './labelPerf';

export interface RawPrintItem {
  data: PlaceholderData;
  copies: number;
}

export interface RawPrintOptions {
  printer: LabelPrinterSelection;
  /** Yazıcı dili. Verilmezse yazıcı seçimindeki dil, o da yoksa PPLA. */
  language?: PrinterLanguage;
  /** İlerleme bildirimi (0-1) */
  onProgress?: (done: number, total: number) => void;
  /** Yazıcı ısı/koyuluk ayarı 0-20 */
  heat?: number;
  /** Eşikleme değeri (0-765) */
  threshold?: number;
}

/**
 * Bir turda rasterlenecek etiket sayısı.
 *
 * Raster üretimi artık etiket başına birkaç milisaniye; gruplamanın amacı
 * ilk etiketin yazıcıya erken ulaşması ve bellekte aynı anda yüzlerce
 * mürekkep matrisi tutmamak.
 */
const RASTER_BATCH_SIZE = 16;

export interface BuildPayloadOptions {
  /** Hedef yazıcının komut dili. Verilmezse PPLA. */
  language?: PrinterLanguage;
  /** Isı/koyuluk 0-20 (PPLA ölçeği; PPLB'nin 0-15 aralığına çevrilir) */
  heat?: number;
  /** Eşikleme değeri (0-765) */
  threshold?: number;
}

/**
 * Isı/koyuluk ölçekleri dilden dile farklı: PPLA 0-20, PPLB 0-15, ZPL 0-30.
 * Arayüzde tek bir ölçek (PPLA'nın 0-20'si) kullanılıyor, burada çevriliyor.
 */
function scaleHeat(heat: number, max: number): number {
  return Math.round((Math.min(20, Math.max(0, heat)) * max) / 20);
}

/** Şablon + verilerden ham yazıcı komutlarını üretir */
export async function buildLabelPayloads(
  template: LabelTemplate,
  items: RawPrintItem[],
  options?: BuildPayloadOptions,
): Promise<Uint8Array[]> {
  if (items.length === 0) return [];

  const language = options?.language ?? 'PPLA';
  const rasters = await rasterizeLabels(
    template,
    items.map((item) => item.data),
    { threshold: options?.threshold },
  );

  // Yazdırma genişliği fiziksel etiketle sınırlanır; kafanın tamamına basmak
  // komşu etikete taşma riski demek
  const labelWidthDots = mmToDots(template.labelWidthMm, template.dpi);

  return rasters.map((raster, i) => {
    const copies = items[i].copies;
    const heat = options?.heat;

    switch (language) {
      // Argox PPLB ile Zebra EPL2 aynı `GW` komutunu kullanıyor
      case 'PPLB':
      case 'EPL':
        return buildEplBitmapJob(raster.mono, {
          copies,
          darkness: heat !== undefined ? scaleHeat(heat, 15) : undefined,
          labelWidthDots,
        });

      case 'ZPL':
        return buildZplBitmapJob(raster.mono, {
          copies,
          darkness: heat !== undefined ? scaleHeat(heat, 30) : undefined,
          labelWidthDots,
        });

      default: {
        const bmp = encodeBmp1Bit(raster.mono);
        return buildPplaBitmapJob(bmp.bytes, {
          copies,
          heat,
          // Konum ötelemesi tasarımın içinde; etiket başlangıcına hizala
          offsetXMm: 0,
          offsetYMm: 0,
        });
      }
    }
  });
}

/** Tek etiket için ham komut — tasarımcıdaki test baskısı bunu kullanıyor */
export async function buildLabelPayload(
  template: LabelTemplate,
  data: PlaceholderData,
  copies: number,
  options?: BuildPayloadOptions,
): Promise<Uint8Array> {
  const [payload] = await buildLabelPayloads(template, [{ data, copies }], options);
  if (!payload) throw new Error('Etiket içeriği oluşturulamadı.');
  return payload;
}

/**
 * Kullanılacak yazıcı dilini belirler.
 * Öncelik: çağrı parametresi → yazıcı seçimi → şablon (BROWSER değilse) → PPLA
 */
export function resolveLanguage(
  template: LabelTemplate,
  // Yazıcı henüz seçilmemişken de çağrılıyor (dil etiketini göstermek için)
  options: { language?: PrinterLanguage; printer?: LabelPrinterSelection | null },
): PrinterLanguage {
  if (options.language) return options.language;
  if (options.printer?.language) return options.printer.language;
  if (template.printerLanguage && template.printerLanguage !== 'BROWSER') {
    return template.printerLanguage;
  }
  return 'PPLA';
}

/**
 * Seçili ürünleri sessizce basar.
 * Her ürün için ayrı raster üretilir; aynı ürünün kopyaları yazıcının
 * kendi sayaç komutuyla (Q) basılır — veri bir kez gönderilir.
 */
export async function printLabelsRaw(
  template: LabelTemplate,
  items: RawPrintItem[],
  options: RawPrintOptions,
): Promise<{ printed: number; bytes: number }> {
  if (!items.length) {
    throw new LocalPrintError('Yazdırılacak etiket yok.', 'NO_ITEMS');
  }

  // Şablonun dili tarayıcı çıktısı için; ham gönderimde yazıcının dili geçerli.
  // Şablonda özel bir etiket dili seçilmişse ona saygı gösterilir.
  const language = resolveLanguage(template, options);
  if (!RAW_SUPPORTED_LANGUAGES.includes(language)) {
    throw new LocalPrintError(
      `${language} dili için doğrudan yazdırma henüz desteklenmiyor.`,
      'UNSUPPORTED_LANGUAGE',
    );
  }

  let printed = 0;
  let bytes = 0;

  const buildPerf = createPerfAccumulator('Ham baskı · raster üretimi (parti)');
  const sendPerf = createPerfAccumulator('Ham baskı · yazıcıya gönderim');
  perfLog(`Ham baskı başlıyor: ${items.length} etiket, ${language}, parti ${RASTER_BATCH_SIZE}`);

  for (let start = 0; start < items.length; start += RASTER_BATCH_SIZE) {
    const batch = items.slice(start, start + RASTER_BATCH_SIZE);

    const payloads = await buildPerf.run(() =>
      buildLabelPayloads(template, batch, {
        language,
        heat: options.heat,
        threshold: options.threshold,
      }),
    );

    for (let i = 0; i < payloads.length; i++) {
      const index = start + i;
      const result = await sendPerf.run(() =>
        sendRawToPrinter(payloads[i], options.printer, {
          jobName: `Etiket ${index + 1}/${items.length}`,
        }),
      );

      bytes += result.bytes;
      printed += batch[i].copies;
      options.onProgress?.(index + 1, items.length);
    }
  }

  buildPerf.summary();
  sendPerf.summary();

  return { printed, bytes };
}
